import * as React from 'react'
import { useRef, useState } from 'react'
import clx from 'classnames'

import { Doughnut } from 'react-chartjs-2'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  type ChartEvent,
  type ActiveElement,
  type ChartOptions,
} from 'chart.js'
import { useClickOutside } from '@/app/hooks/useClickOutside'
ChartJS.register(ArcElement, Tooltip, Legend)

type Props = {
  values: number[]
  cutout?: number
  highlightCutout?: number
  onDoughnutHover?: (index: number | null) => void
  doughnutRef?: React.RefObject<HTMLDivElement>
  className?: string
}

export const MultiLayerDonutChart = ({
  className,
  doughnutRef,
  values,
  cutout = 84,
  highlightCutout = 75,
  onDoughnutHover,
}: Props) => {
  const { t } = useLocalize()
  const chartRefInner = useRef<ChartJS<'doughnut', number[], unknown> | null>(
    null,
  )
  const [highlightIndex, setHighlightIndex] = useState<number | null>(null)

  useClickOutside({
    ref: doughnutRef,
    handler: () => {
      setHighlightIndex(null)
      onDoughnutHover?.(null)
    },
  })

  const hasValues =
    Array.isArray(values) && values.some((value) => Number(value) > 0)
  const chartValues = hasValues ? values : [100]
  const colors = hasValues ? ['#2448BB', '#3C7CFF', '#52BEFF'] : ['#D1E2F6']

  const dataOuter = {
    datasets: [
      {
        data: chartValues,
        backgroundColor: colors,
        borderWidth: 0,
      },
    ],
  }

  const handleHover = (
    _event: ChartEvent,
    activeElements: ActiveElement[] | null,
  ) => {
    if (activeElements && activeElements.length > 0) {
      const idx = activeElements[0].index
      if (idx === highlightIndex) return
      setHighlightIndex(idx)
      onDoughnutHover?.(idx)
    } else {
      if (highlightIndex === null) return
      setHighlightIndex(null)
      onDoughnutHover?.(null)
    }
  }

  const optionsOuter: ChartOptions<'doughnut'> = {
    cutout: `${cutout}%`,
    interaction: {
      mode: 'nearest',
      intersect: true,
    },
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false },
    },
    onHover: handleHover,
  }

  const dataInner = {
    datasets: [
      {
        data: chartValues,
        backgroundColor: colors.map((c, i) =>
          i === highlightIndex ? c : 'rgba(0,0,0,0)',
        ),
        borderWidth: 0,
      },
    ],
  }
  const optionsInner: ChartOptions<'doughnut'> = {
    cutout: `${highlightCutout}%`,
    interaction: {
      mode: 'nearest',
      intersect: true,
    },
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false },
    },
    animation: false,
    onHover: handleHover,
  }

  const sum = React.useMemo(() => {
    if (!values) return 0
    return values.reduce((acc, val) => acc + val, 0)
  }, [values])

  return (
    <div
      className={clx('tw-relative tw-h-[204px] tw-w-[204px]', className)}
      ref={doughnutRef}
    >
      <div
        className="tw-absolute tw-inset-0"
        style={{
          zIndex: 1,
          opacity: highlightIndex === null ? 1 : 0.3,
        }}
      >
        <Doughnut ref={chartRefInner} data={dataOuter} options={optionsOuter} />
      </div>

      {highlightIndex !== null && (
        <div
          className="tw-absolute tw-inset-0 tw-z-[2]"
          style={{ pointerEvents: 'none' }}
        >
          <Doughnut data={dataInner} options={optionsInner} />
        </div>
      )}

      <div
        className={clx(
          'tw-absolute tw-bottom-0 tw-left-1/2 tw-top-1/2 tw-z-[3] tw-flex tw-min-w-fit tw--translate-x-1/2 tw--translate-y-1/2 tw-transform tw-flex-col tw-items-center tw-justify-center tw-text-center tw-text-2xl tw-font-bold tw-text-content-base',
          { 'tw-opacity-60': highlightIndex !== null },
        )}
      >
        <p className="tw-mb-0">{sum.toLocaleString()} h</p>
        <div className="tw-min-w-36 tw-text-xs tw-font-normal tw-text-content-base/80">
          {t('dashboard.total-work-time')}
        </div>
      </div>
    </div>
  )
}
